import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    guestOnly?: boolean
    title?: string
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: () => (getStoredUser() ? '/dashboard' : '/login') },

  { path: '/login', name: 'login', component: () => import('@/views/auth/LoginView.vue'), meta: { guestOnly: true, title: 'Sign In' } },
  { path: '/register', name: 'register', component: () => import('@/views/auth/RegisterView.vue'), meta: { guestOnly: true, title: 'Create Account' } },

  { path: '/dashboard', name: 'dashboard', component: () => import('@/views/DashboardView.vue'), meta: { requiresAuth: true, title: 'Projects' } },

  { path: '/projects/:id', name: 'project', component: () => import('@/views/ProjectView.vue'), meta: { requiresAuth: true, title: 'Issues' } },
  {
    path: '/projects/:id/settings',
    component: () => import('@/views/project-settings/ProjectSettingsLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: to => `/projects/${to.params.id}/settings/general` },
      { path: 'general', name: 'project-settings-general', component: () => import('@/views/project-settings/GeneralView.vue'), meta: { requiresAuth: true, title: 'General — Project Settings' } },
      { path: 'keys', name: 'project-settings-keys', component: () => import('@/views/project-settings/KeysView.vue'), meta: { requiresAuth: true, title: 'Installation — Project Settings' } },
      { path: 'status-page', name: 'project-settings-status-page', component: () => import('@/views/project-settings/StatusPageView.vue'), meta: { requiresAuth: true, title: 'Status Page — Project Settings' } },
    ],
  },

  { path: '/projects/:projectId/issues/:issueId', name: 'issue', component: () => import('@/views/IssueView.vue'), meta: { requiresAuth: true, title: 'Issue' } },
  { path: '/projects/:projectId/events/:eventId', name: 'event', component: () => import('@/views/EventView.vue'), meta: { requiresAuth: true, title: 'Event' } },
  { path: '/projects/:id/alerts', name: 'project-alerts', component: () => import('@/views/AlertsView.vue'), meta: { requiresAuth: true, title: 'Alerts' } },
  { path: '/projects/:id/monitors', name: 'project-monitors', component: () => import('@/views/MonitorsView.vue'), meta: { requiresAuth: true, title: 'Monitors' } },
  { path: '/projects/:id/performance', name: 'project-performance', component: () => import('@/views/PerformanceView.vue'), meta: { requiresAuth: true, title: 'Performance' } },
  { path: '/projects/:id/source-maps', name: 'project-source-maps', component: () => import('@/views/SourceMapsView.vue'), meta: { requiresAuth: true, title: 'Source Maps' } },
  { path: '/projects/:id/inbound-filters', name: 'project-inbound-filters', component: () => import('@/views/InboundFiltersView.vue'), meta: { requiresAuth: true, title: 'Inbound Filters' } },
  { path: '/projects/:id/ownership-rules', name: 'project-ownership-rules', component: () => import('@/views/OwnershipRulesView.vue'), meta: { requiresAuth: true, title: 'Ownership Rules' } },
  { path: '/projects/:id/releases', name: 'project-releases', component: () => import('@/views/ReleaseHealthView.vue'), meta: { requiresAuth: true, title: 'Release Health' } },
  { path: '/projects/:id/analytics', name: 'project-analytics', component: () => import('@/views/AnalyticsView.vue'), meta: { requiresAuth: true, title: 'Analytics' } },
  { path: '/analytics', name: 'workspace-analytics', component: () => import('@/views/WorkspaceAnalyticsView.vue'), meta: { requiresAuth: true, title: 'Analytics' } },

  {
    path: '/settings',
    component: () => import('@/views/settings/SettingsLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: 'account', redirect: '/settings/account/details' },
      { path: 'account/details', name: 'account-details', component: () => import('@/views/account/AccountDetailsView.vue'), meta: { title: 'Details — Account' } },
      { path: 'account/security', name: 'account-security', component: () => import('@/views/account/AccountSecurityView.vue'), meta: { title: 'Security — Account' } },
      { path: 'account/email', name: 'account-email', component: () => import('@/views/account/AccountEmailView.vue'), meta: { title: 'Email — Account' } },
      { path: 'account/notifications', name: 'account-notifications', component: () => import('@/views/account/AccountNotificationsView.vue'), meta: { title: 'Notifications — Account' } },
      { path: '', redirect: '/settings/general' },
      { path: 'general', name: 'settings-general', component: () => import('@/views/settings/GeneralView.vue'), meta: { title: 'General — Settings' } },
      { path: 'members', name: 'settings-members', component: () => import('@/views/settings/MembersView.vue'), meta: { title: 'Members — Settings' } },
      { path: 'invitations', redirect: '/settings/members' },
      { path: 'integrations/slack', name: 'settings-slack', component: () => import('@/views/settings/SlackView.vue'), meta: { title: 'Slack — Settings' } },
      { path: 'integrations/pagerduty', name: 'settings-pagerduty', component: () => import('@/views/settings/PagerDutyView.vue'), meta: { title: 'PagerDuty — Settings' } },
      { path: 'security/audit-log', name: 'settings-audit-log', component: () => import('@/views/settings/AuditLogView.vue'), meta: { title: 'Audit Log — Settings' } },
      { path: 'danger', name: 'settings-danger', component: () => import('@/views/settings/DangerView.vue'), meta: { title: 'Danger Zone — Settings' } },
    ],
  },

  { path: '/status/:slug', name: 'public-status', component: () => import('@/views/PublicStatusView.vue'), meta: { title: 'Status Page' } },

  { path: '/:pathMatch(.*)*', redirect: '/dashboard' },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

function getStoredUser(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem('user')
    if (!raw || raw === 'undefined' || raw === 'null') return null
    return JSON.parse(raw) as Record<string, unknown>
  } catch {
    localStorage.removeItem('user')
    return null
  }
}

router.beforeEach(async (to, _from, next) => {
  const storedUser = getStoredUser()
  const isAuthenticated = storedUser !== null

  if (to.meta.guestOnly && isAuthenticated) return next('/dashboard')
  if (to.meta.requiresAuth && !isAuthenticated) return next({ name: 'login', query: { redirect: to.fullPath } })

  if (to.meta.requiresAuth && isAuthenticated) {
    const { useSessionStore } = await import('@/store/useSessionStore')
    const session = useSessionStore()
    if (!session.currentWorkspace) {
      const result = await session.fetchSession()
      if (result === 'error') {
        // Server error (e.g. 502 during deploy) — preserve session, let navigation proceed
        return next()
      }
      if (!session.currentWorkspace) {
        session.clearSession()
        return next({ name: 'login' })
      }
    }
  }

  next()
})

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} — Beacon` : 'Beacon'
})

router.onError((err, to) => {
  const isChunkError =
    err.message.includes('Failed to fetch dynamically imported module') ||
    err.message.includes('ChunkLoadError')
  if (isChunkError) {
    window.location.href = to.fullPath
  }
})

export default router
